import { MediaStream, MediaStreamError } from 'frontend/types'
import { UrPlayerConfig, Plugin } from './types'
import UrPlayer, { PlayerSetupOptions } from './urPlayer'
import { fetchWithTimeout } from './utils/urPlayerUtils'
import { isURAccessExclusive } from 'frontend/utils/helpers'

export const GEOLOCATION_ERROR =
  'Det här programmet får på grund av upphovsrätt bara visas i Sverige'

export const FAILED_TO_FETCH_MEDIA_STREAM =
  'Vi har tekniska problem. Försök igen om en stund.'

export type PreSetupHook = (
  id: string,
  urPlayerConfig: UrPlayerConfig,
) => Partial<PlayerSetupOptions> | null

class UrPlayerManager {
  private static readonly instance: UrPlayerManager

  private installing: Promise<void> | null = null

  private readonly MEDIA_STREAM_FETCH_TIMEOUT = 4000

  constructor(
    private readonly scriptSrc: string,
    private readonly licenseKey: string,
  ) {}

  static getInstance = (scriptSrc: string, licenseKey: string) => {
    if (!UrPlayerManager.instance) {
      return new UrPlayerManager(scriptSrc, licenseKey)
    }

    if (UrPlayerManager.instance.scriptSrc !== scriptSrc) {
      throw new Error('scriptSrc cannot differ for different players.')
    }

    return UrPlayerManager.instance
  }

  createPlayer = async (
    elementId: string,
    config: UrPlayerConfig,
    preSetupHooks: PreSetupHook[] = [],
    plugins: Plugin[] = [],
  ) => {
    await this.install()
    if (window.jwplayer) {
      window.jwplayer.key = this.licenseKey
    }

    let mediaStream: MediaStream | MediaStreamError

    try {
      if (isURAccessExclusive(config.platforms)) {
        mediaStream = await fetchWithTimeout<MediaStream | MediaStreamError>(
          `${process.env.NEXT_PUBLIC_MEDIA_STREAM_ENDPOINT_URACCESS}/${config.urAssetId}`,
          this.MEDIA_STREAM_FETCH_TIMEOUT,
        )
      } else {
        mediaStream = await fetchWithTimeout<MediaStream | MediaStreamError>(
          `${process.env.NEXT_PUBLIC_MEDIA_STREAM_ENDPOINT}/${config.urAssetId}`,
          this.MEDIA_STREAM_FETCH_TIMEOUT,
        )
      }
    } catch {
      throw new Error(FAILED_TO_FETCH_MEDIA_STREAM)
    }

    if ('statusCode' in mediaStream) {
      if (mediaStream.statusCode === 403) {
        throw new Error(GEOLOCATION_ERROR)
      }

      if (mediaStream.statusCode === 404) {
        throw new Error(FAILED_TO_FETCH_MEDIA_STREAM)
      }
    }
    mediaStream = mediaStream as MediaStream

    const urPlayerConfig = { ...config, mediaStream }

    const setupOptions = this.runPreSetupHooks(
      elementId,
      urPlayerConfig,
      preSetupHooks,
    )

    const urPlayer = new UrPlayer(elementId, urPlayerConfig)
    await urPlayer.setup(setupOptions)

    urPlayer.registerPlugins(plugins)

    return urPlayer
  }

  private readonly install = () => {
    if (!this.installing) {
      this.installing = new Promise((resolve, reject) => {
        const jwPlayerScript = document.createElement('script')
        jwPlayerScript.src = this.scriptSrc
        jwPlayerScript.onload = () => resolve()
        jwPlayerScript.onerror = () =>
          reject(
            new Error(`Unable to load player source from ${this.scriptSrc}`),
          )
        document.head.appendChild(jwPlayerScript)
      })
    }
    return this.installing
  }

  private readonly runPreSetupHooks = (
    elementId: string,
    urPlayerConfig: UrPlayerConfig,
    hooks: PreSetupHook[],
  ): Record<string, string | number | boolean> => {
    const result = hooks.map(hook => hook(elementId, urPlayerConfig))
    const filteredResult = result.filter(
      Boolean,
    ) as Partial<PlayerSetupOptions>[]

    return filteredResult.reduce(this.mergeObjects, {})
  }

  private readonly mergeObjects = (acc = {}, obj = {}) => ({ ...acc, ...obj })
}

export default UrPlayerManager
